'use client'

import WhatToExpectSection from '@/components/whatToExpect'
import type { Step } from '@/components/whatToExpect'
import AllServicesHeroSection from '@/components/services/allServicesHeroSection'
import {
  BookOpenCheck,
  MessageSquare,
  ClipboardList,
  Brain,
  Zap,
  NotebookText,
} from 'lucide-react'
import AllServicesSubSection from '@/components/services/allServicesSubSection'
import PsychiatricEvaluationsBenefits from '@/components/services/psychiatric-evaluation/psychiatricEvaluationsBenefits'
import PhilosophySection from '@/components/services/Philosophy'
import ServiceBookingCTA from '@/components/services/ServiceBookingCTA'

const steps: Step[] = [
  {
    icon: MessageSquare,
    title: 'Warm Welcome & Initial Discussion',
    description:
      "Your evaluation begins with a confidential conversation. We'll discuss your current concerns, what you hope to achieve, and answer any immediate questions you have.",
  },
  {
    icon: ClipboardList,
    title: 'Comprehensive Information Gathering',
    description:
      "We'll explore your symptoms, history, lifestyle factors, and social supports. Questionnaires may be used for deeper insight.",
  },
  {
    icon: Brain,
    title: 'Clinical Assessment & Dialogue',
    description:
      'Your psychiatrist assesses thought processes and emotional well‑being through respectful dialogue.',
  },
  {
    icon: Zap,
    title: 'Understanding Your Diagnosis',
    description:
      "We'll review potential diagnoses in clear, jargon‑free language so you fully understand the findings.",
  },
  {
    icon: NotebookText,
    title: 'Collaborative Treatment Planning',
    description:
      'Together we craft a personalized plan — therapy, medication, lifestyle changes — reflecting your preferences.',
  },
]

const PsychiatricEvaluationPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <AllServicesHeroSection
        title="Psychiatric Evaluation & Diagnosis: Understanding Your Journey to Wellness"
        description="A thorough and compassionate assessment is the first step towards effective mental health treatment and a brighter future. We're here to listen and help you find clarity."
      />

      {/* What is a Psychiatric Evaluation */}
      <AllServicesSubSection
        title="Gaining Clarity: What is a Psychiatric Evaluation?"
        para1="A psychiatric evaluation at ReACH Psychiatry is a detailed process designed to understand your unique mental health concerns. It typically involves an in-depth discussion about your symptoms, personal and family history, lifestyle, and treatment goals."
        para2="Our goal is to gather a complete picture to inform an accurate diagnosis and develop a personalized, effective treatment plan in collaboration with you. This process is the foundation of effective mental health care, providing the insights needed to guide your path to wellness."
        icon={BookOpenCheck}
      />

      {/* What to Expect */}
      <section className="py-12 sm:py-16 lg:py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="relative">
            {/* Decorative blur — hidden on mobile to keep things clean */}
            <div className="hidden sm:flex absolute inset-0 items-center justify-center opacity-10 pointer-events-none">
              <div className="w-96 h-96 bg-gradient-to-br from-emerald-200 to-teal-200 rounded-full blur-3xl" />
            </div>
            <div className="relative z-10 max-w-7xl mx-auto">
              <WhatToExpectSection
                steps={steps}
                mainTitle="Your Evaluation Journey: What to Expect at ReACH"
                mainDescription="We strive to make your evaluation process as comfortable and clear as possible. Here's what you can typically expect:"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <PsychiatricEvaluationsBenefits />

      {/* Philosophy */}
      <PhilosophySection
        title="Our Approach: Compassion Meets Science in Every Assessment"
        paragraph="At ReACH Psychiatry, your evaluation is far more than a clinical checklist. We are deeply committed to our ethos of 'Where Compassion Meets Science.' This means you will always be treated with profound respect in a non-judgmental space. Our expert psychiatrists combine their extensive clinical knowledge with genuine empathy, ensuring a diagnostic process that is both precise and human-centered."
      />

      {/* Next action */}
      <ServiceBookingCTA />
    </div>
  )
}

export default PsychiatricEvaluationPage
